import React, { Suspense, useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography,
  CircularProgress, Divider, Tabs, Tab,
} from '@mui/material';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { useSearchParams } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { PQCD } from '../../lib/pqcdMetrics';
import { calcItemScore } from '../../lib/pqcdTrend';
import usePqcdData from '../../lib/usePqcdData';
import BreadcrumbsContainer from '../../containers/common/breadcurmbs/BreadcrumbsContainer';
import KpiReportTemplate from '../../components/kpimng/KpiReportTemplate';
import * as kpiApi from '../../lib/api/kpiReport';
// 생산/품질 탭은 각 단독 페이지(KpiProductionPage/KpiQualityPage)와 같은 컬럼·같은 계산
// 코드를 그대로 재사용한다 - 여기서 따로 베껴 쓰면 다시 값이 갈릴 수 있다.
import {
  fetchFn as productionFetchFn,
  chartConfig as productionChartConfig,
  columns as productionColumns,
  summaryFn as productionSummaryFn,
  EqpKpiSection,
} from './KpiProductionPage';
import {
  processChartConfig,
  processColumns,
  processSummaryFn,
  qualityChartConfig,
  qualityColumns,
  qualitySummaryFn,
} from './KpiQualityPage';

const thisYear = String(new Date().getFullYear());

// ── 대시보드 요약 카드 ──────────────────────────────────────────────
const StatCard = ({ title, items, loading }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Grid container spacing={1}>
          {items.map(({ label, value, unit, color }) => (
            <Grid item xs={6} key={label}>
              <Box sx={{ textAlign: 'center', py: 0.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color }}>
                  {value}<Typography component="span" variant="caption" sx={{ ml: 0.3 }}>{unit}</Typography>
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </CardContent>
  </Card>
);

// ── 대시보드 탭 내용 ────────────────────────────────────────────────
const DashboardTab = () => {
  const [production,    setProduction]    = useState([]);
  const [processDefect, setProcessDefect] = useState([]);
  const [quality,       setQuality]       = useState([]);
  const [delivery,      setDelivery]      = useState([]);
  const [loadingP,      setLoadingP]      = useState(true);
  const [loadingPD,     setLoadingPD]     = useState(true);
  const [loadingQ,      setLoadingQ]      = useState(true);
  const [loadingD,      setLoadingD]      = useState(true);

  useEffect(() => {
    const params = { searchYear: thisYear };

    // productionFetchFn 은 KpiProductionPage 와 같은 병합(생산+공정불량률) 로직을 그대로 쓴다.
    productionFetchFn(params)
      .then(r => setProduction(r.data.list || []))
      .catch(() => setProduction([]))
      .finally(() => setLoadingP(false));

    // 공정불량률 카드는 정본인 GET /kpi/process-defect 원본 리스트로 계산한다(위 병합 리스트는
    // defectQty 필드명이 달라 processSummaryFn 이 못 읽는다 - KpiQualityPage 블록1과 동일하게 씀).
    kpiApi.getProcessDefectKpi(params)
      .then(r => setProcessDefect(r.data.list || []))
      .catch(() => setProcessDefect([]))
      .finally(() => setLoadingPD(false));

    kpiApi.getQualityKpi(params)
      .then(r => setQuality(r.data.list || []))
      .catch(() => setQuality([]))
      .finally(() => setLoadingQ(false));

    kpiApi.getDeliveryKpi(params)
      .then(r => setDelivery(r.data.list || []))
      .catch(() => setDelivery([]))
      .finally(() => setLoadingD(false));
  }, []);

  // ── 생산 요약 / 공정 불량률 요약 / 완제품 불량률 요약
  // 아래 탭(ProductionTab/QualityTab)과 정확히 같은 계산 코드(productionSummaryFn/
  // processSummaryFn/qualitySummaryFn, KpiProductionPage·KpiQualityPage 에서 import)를
  // 그대로 재사용한다 - 여기서 따로 계산하면 탭과 카드 숫자가 다시 갈릴 수 있다.
  const prodSummary    = productionSummaryFn(production);
  const processSummary = processSummaryFn(processDefect);
  const finalSummary   = qualitySummaryFn(quality);

  const totalProduced = production.reduce((s, r) => s + (Number(r.producedQty) || 0), 0);
  // 양품률은 KpiProductionPage 작업(불량·양품 지표를 품질 리포트로 이동)으로 prodSummary 에서
  // 빠졌다 - processSummary(공정불량률, KpiQualityPage 계산 재사용)에 같은 값이 이미 있어 거기서
  // 가져온다. totalDefect 도 원래부터 processSummary 출처였다(같은 정본, GET /kpi/process-defect).
  const goodRate       = processSummary[3].value;   // 양품률(검사공정 기준)
  const totalDefect    = processSummary[1].value;   // 검사공정 불량수량
  const throughput     = prodSummary[2].value;       // Throughput (요약카드에서 불량·양품 항목이 빠지며 인덱스가 밀렸다)

  const processDefectRate = processSummary[2].value; // 공정 불량률(수량기준)
  const totalProcessOutput = processSummary[0].value; // 검사공정 생산수량

  const finalDefectRate    = finalSummary[0].value;  // 완제품 불량률(건수기준)
  const totalFinalInsp     = finalSummary[1].value;  // 완품검사 판정건수
  const totalFinalFail     = finalSummary[2].value;  // 불합격(FAIL) 건수

  // ── 납기 요약
  const totalDeliv  = delivery.reduce((s, r) => s + (Number(r.totalCnt)  || 0), 0);
  const onTimeCnt   = delivery.reduce((s, r) => s + (Number(r.onTimeCnt) || 0), 0);
  const otdRate     = totalDeliv > 0 ? (onTimeCnt / totalDeliv * 100).toFixed(1) : '-';
  const validLead   = delivery.filter(r => r.avgLeadDays != null);
  const avgLead     = validLead.length > 0
    ? (validLead.reduce((s, r) => s + Number(r.avgLeadDays), 0) / validLead.length).toFixed(1) : '-';

  // ── 월별 추이 차트 데이터
  const monthlyMap = {};
  production.forEach(r => {
    const ym = r.kpiYm; if (!ym) return;
    if (!monthlyMap[ym]) monthlyMap[ym] = { ym };
    monthlyMap[ym].produced = (monthlyMap[ym].produced || 0) + (Number(r.producedQty) || 0);
    monthlyMap[ym].defect   = (monthlyMap[ym].defect   || 0) + (Number(r.defectQty)   || 0);
  });
  quality.forEach(r => {
    const ym = r.kpiYm; if (!ym) return;
    if (!monthlyMap[ym]) monthlyMap[ym] = { ym };
    monthlyMap[ym].failQty  = (monthlyMap[ym].failQty  || 0) + (Number(r.failQty)     || 0);
  });
  const monthlyData = Object.values(monthlyMap).sort((a, b) => a.ym > b.ym ? 1 : -1);

  // ── "PQCD 종합 스코어" 레이더 (Phase 4, 2026-10 - 기존 "KPI 종합 스코어"(otdScore·
  //     finalQualScore·goodScore 3항목) 대체) ────────────────────────────────────
  // 메인 대시보드와 같은 자동 베이스라인(usePqcdData/calcItemScore)을 쓴다 - 이 탭만 따로
  // 계산하면 메인과 숫자가 갈릴 수 있어 공용 훅으로 뺐다(사용자 승인 사항).
  // 축은 P/Q/C/D 4개, 축 점수는 "그 축에 속한 항목들의 점수 평균"이다(항목 중 데이터 없는
  // 것은 평균에서 빼고, 축 전체에 데이터 있는 항목이 하나도 없으면 그 축은 0점+데이터 없음).
  const {
    pqcdThroughput, pqcdDefect, pqcdWipResult, pqcdDeliveryShorten,
    cardProduced, cardOtd,
  } = usePqcdData();

  const axisScore = (items) => {
    const scores = items
      .map(({ current, base, higherIsBetter }) => calcItemScore(current, base, higherIsBetter))
      .filter((s) => s != null);
    if (scores.length === 0) return { score: 0, noData: true };
    return { score: scores.reduce((s, v) => s + v, 0) / scores.length, noData: false };
  };

  // P = 생산완료수량 + 시간당 생산량(둘 다 높을수록 좋음)
  const pAxis = axisScore([
    { current: cardProduced.current, base: cardProduced.base, higherIsBetter: true },
    { current: pqcdThroughput.current, base: pqcdThroughput.base, higherIsBetter: true },
  ]);
  // Q = 완제품 불량률(낮을수록 좋음)
  const qAxis = axisScore([
    { current: pqcdDefect.current, base: pqcdDefect.base, higherIsBetter: false },
  ]);
  // C = 재공재고(낮을수록 좋음)
  const cAxis = axisScore([
    { current: pqcdWipResult.current, base: pqcdWipResult.base, higherIsBetter: false },
  ]);
  // D = 제조납기준수율(높을수록 좋음) + 납기 단축률(계약~납품 소요일수 기준 - 적을수록 좋음,
  // pqcdDeliveryShorten.current/base 는 %가 아니라 "일수" 원값이다 - calcDeliveryShortenRate 참고)
  const dAxis = axisScore([
    { current: cardOtd.current, base: cardOtd.base, higherIsBetter: true },
    { current: pqcdDeliveryShorten.current, base: pqcdDeliveryShorten.base, higherIsBetter: false },
  ]);

  const pqcdScoreRadarData = [
    { subject: pAxis.noData ? 'P 생산 (데이터 없음)' : 'P 생산', score: pAxis.score, ref: 100 },
    { subject: qAxis.noData ? 'Q 품질 (데이터 없음)' : 'Q 품질', score: qAxis.score, ref: 100 },
    { subject: cAxis.noData ? 'C 원가 (데이터 없음)' : 'C 원가', score: cAxis.score, ref: 100 },
    { subject: dAxis.noData ? 'D 납기 (데이터 없음)' : 'D 납기', score: dAxis.score, ref: 100 },
  ];

  return (
    <>
      {/* 요약 카드 4개 - 생산 / 공정 불량률(수량기준) / 완제품 불량률(건수기준) / 납기.
          두 불량률은 계산 단위가 달라 카드를 분리했다(작업 지시대로 통일하지 않음). */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="생산 KPI"
            loading={loadingP}
            items={[
              { label: '총 생산완료수량', value: totalProduced.toLocaleString(), unit: '개', color: '#1976d2' },
              { label: '양품률',      value: goodRate,                        unit: '%',  color: goodRate >= 95 ? '#388e3c' : '#f57c00' },
              { label: '검사공정 불량수량', value: totalDefect,               unit: '개', color: '#f44336' },
              { label: 'Throughput', value: throughput,                       unit: '/h', color: '#7b1fa2' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="공정 불량률 (수량기준)"
            loading={loadingPD}
            items={[
              { label: '검사공정 생산수량', value: totalProcessOutput, unit: '개', color: '#1976d2' },
              { label: '공정 불량률',   value: processDefectRate,      unit: '%',  color: '#f44336' },
              { label: '검사공정 불량수량', value: totalDefect,        unit: '개', color: '#f44336' },
              { label: '양품률',        value: goodRate,               unit: '%',  color: '#388e3c' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="완제품 불량률 (건수기준)"
            loading={loadingQ}
            items={[
              { label: '완품검사 판정건수', value: totalFinalInsp,      unit: '건', color: '#1976d2' },
              { label: '완제품 불량률',     value: finalDefectRate,     unit: '%',  color: '#f44336' },
              { label: '불합격(FAIL) 건수', value: totalFinalFail,      unit: '건', color: '#f44336' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="납기 KPI"
            loading={loadingD}
            items={[
              { label: '총 납기건수', value: totalDeliv,  unit: '건', color: '#1976d2' },
              { label: 'OTD 준수율',  value: otdRate,     unit: '%',  color: otdRate >= 90 ? '#388e3c' : '#f44336' },
              { label: '준수건수',    value: onTimeCnt,   unit: '건', color: '#388e3c' },
              { label: '평균리드타임',value: avgLead,     unit: '일', color: '#f57c00' },
            ]}
          />
        </Grid>
      </Grid>

      {/* 하단: 레이더 + 월별 추이 */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>PQCD 종합 스코어</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <RadarChart data={pqcdScoreRadarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                  <PolarRadiusAxis domain={[0, 150]} tick={{ fontSize: 9 }} />
                  <Radar name="기준선" dataKey="ref" stroke="#9e9e9e" strokeDasharray="4 4" fill="none" />
                  <Radar name="점수" dataKey="score" stroke="#1976d2" fill="#1976d2" fillOpacity={0.3} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `${v.toFixed(1)}점`} />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>월별 생산완료 · 불량 추이</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="ym" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="produced" name="생산완료수량" fill={PQCD.P.color} barSize={14} />
                  <Bar dataKey="defect"   name="불량수량" fill={PQCD.Q.color} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

// ── 탭 설정 ─────────────────────────────────────────────────────────
// P·Q·C·D 순서로 둔다(메인 대시보드 카드·게이지와 같은 순서). 아이콘과 색도 메인 카드와 같은 것을
// 써서, 메인에서 본 항목을 여기서 색으로 바로 찾을 수 있게 한다. 순서를 바꿔도 URL(?tab=)은
// key 로 찾으므로 예전 링크(?tab=delivery 등)는 그대로 열린다.
const TABS = [
  { key: 'dashboard',  label: '대시보드', category: null, Icon: DashboardIcon },
  { key: 'production', label: '생산',     category: 'P',  Icon: InventoryIcon },
  { key: 'quality',    label: '품질',     category: 'Q',  Icon: FactCheckIcon },
  { key: 'cost',       label: '원가',     category: 'C',  Icon: WarehouseIcon },
  { key: 'delivery',   label: '납기',     category: 'D',  Icon: TrendingUpIcon },
];
const TAB_KEYS = TABS.map((t) => t.key);

// 메인 KpiCard 의 아이콘 칸과 같은 모양(카테고리 색 사각형 + 흰 아이콘)을 탭 크기로 줄였다.
const TabIcon = ({ category, Icon }) => (
  <Box
    sx={{
      width: 22, height: 22, mr: 1, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: category ? PQCD[category].color : 'grey.500',
    }}
  >
    <Icon sx={{ color: '#fff', fontSize: 15 }} />
  </Box>
);

// ════════════════════════════════════════════════════════════════════
// 메인 통합 페이지
// ════════════════════════════════════════════════════════════════════
const KpiDashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tabIndex = Math.max(0, TAB_KEYS.indexOf(tabParam));

  const handleTabChange = (_, newValue) => {
    setSearchParams(newValue === 0 ? {} : { tab: TAB_KEYS[newValue] });
  };

  return (
    <Box sx={{ p: 3 }}>
      <BreadcrumbsContainer title="목표관리/KPI 리포트" icon="goal" />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        KPI 리포트
      </Typography>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {TABS.map(({ key, label, category, Icon }) => (
          <Tab
            key={key}
            icon={<TabIcon category={category} Icon={Icon} />}
            iconPosition="start"
            label={category ? `${label} (${category})` : label}
            sx={{ minHeight: 48 }}
          />
        ))}
      </Tabs>

      {/* 인덱스가 아니라 key 로 고른다 - 탭 순서를 바꿔도 내용이 엇갈리지 않게 */}
      {TAB_KEYS[tabIndex] === 'dashboard' && <DashboardTab />}
      {TAB_KEYS[tabIndex] === 'production' && <ProductionTab />}
      {TAB_KEYS[tabIndex] === 'quality' && <QualityTab />}
      {TAB_KEYS[tabIndex] === 'delivery' && <DeliveryTab />}
      {TAB_KEYS[tabIndex] === 'cost' && (
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}>
          <CostTab />
        </Suspense>
      )}
    </Box>
  );
};

// ── 생산 탭 ─────────────────────────────────────────────────────────
// KpiProductionPage 와 같은 chartConfig/columns/summaryFn(import) 을 그대로 쓴다.
const ProductionTab = () => (
  <>
    <KpiReportTemplate
      title="생산 KPI"
      subtitle="※ 불량수량·양품률(검사공정 기준)은 품질 리포트로 이동했습니다 - 여기서는 생산 지표만 표시합니다."
      fetchFn={productionFetchFn}
      chartConfig={productionChartConfig}
      columns={productionColumns}
      summaryFn={productionSummaryFn}
    />
    {/* 설비 가동은 원가에서 여기로 옮겼다. 장비별 집계라 KpiReportTemplate(기간별)로는
        못 그려서 별도 섹션으로 둔다. */}
    <Box sx={{ mt: 5 }}>
      <EqpKpiSection />
    </Box>
  </>
);

// ── 품질 탭 ─────────────────────────────────────────────────────────
// KpiQualityPage 와 같은 두 블록(공정 불량률 · 완제품 불량률)을 그대로 재사용한다.
const QualityTab = () => (
  <>
    <Box sx={{ mb: 5 }}>
      <KpiReportTemplate
        title="공정 불량률"
        subtitle="※ 검사를 실시한 공정(QC_YN='Y')만 모집단인 수량 기준 지표입니다. 계획 상태 제한이 없어 진행중·QC_FAILED 계획도 포함합니다(GET /kpi/process-defect)."
        fetchFn={kpiApi.getProcessDefectKpi}
        chartConfig={processChartConfig}
        columns={processColumns}
        summaryFn={processSummaryFn}
      />
    </Box>
    <Box>
      <KpiReportTemplate
        title="완제품 불량률 (완품검사 기준)"
        subtitle="※ 완제품 불량률은 완품검사 판정 건수 기준입니다(부분 불량 없음). 나머지 검사수량·품질률·PPM 컬럼은 완품검사를 수량으로 재는 레거시 지표입니다."
        fetchFn={kpiApi.getQualityKpi}
        chartConfig={qualityChartConfig}
        columns={qualityColumns}
        summaryFn={qualitySummaryFn}
      />
    </Box>
  </>
);

// ── 납기 탭 ─────────────────────────────────────────────────────────
const fmtD = (v, d = 1) => (v != null ? Number(v).toFixed(d) : '-');

const deliveryChartConfig = {
  type: 'bar',
  bars: [
    { key: 'otdRate',      name: '납기준수율(%)', color: '#1976d2' },
    { key: 'avgLeadDays',  name: '평균 리드타임(일)', color: '#f57c00' },
  ],
};

const deliveryColumns = [
  { field: 'kpiYm',       headerName: '년월',       width: 90 },
  { field: 'kpiYear',     headerName: '연도',       width: 70 },
  { field: 'productNm',   headerName: '제품명',     minWidth: 160, flex: 1 },
  { field: 'totalCnt',    headerName: '전체건수',   width: 100 },
  { field: 'onTimeCnt',   headerName: '준수건수',   width: 100 },
  {
    field: 'otdRate',
    headerName: 'OTD(%)',
    width: 100,
    renderCell: ({ value }) => {
      if (value == null) return '-';
      const v = Number(value);
      const color = v >= 90 ? '#388e3c' : v >= 70 ? '#f57c00' : '#f44336';
      return <span style={{ color, fontWeight: 700 }}>{v.toFixed(1)}%</span>;
    },
  },
  { field: 'avgLeadDays', headerName: '평균리드타임(일)', width: 130, valueFormatter: (v) => fmtD(v) },
];

const deliverySummaryFn = (list) => {
  const totalCnt  = list.reduce((s, r) => s + (Number(r.totalCnt)  || 0), 0);
  const onTimeCnt = list.reduce((s, r) => s + (Number(r.onTimeCnt) || 0), 0);
  const otdRate   = totalCnt > 0 ? (onTimeCnt / totalCnt * 100).toFixed(1) : '-';
  const validLead = list.filter(r => r.avgLeadDays != null);
  const avgLead   = validLead.length > 0
    ? (validLead.reduce((s, r) => s + Number(r.avgLeadDays), 0) / validLead.length).toFixed(1)
    : '-';
  return [
    { label: '전체 납기건수',   value: totalCnt,  unit: '건', color: '#1976d2' },
    { label: '납기 준수건수',   value: onTimeCnt, unit: '건', color: '#388e3c' },
    { label: 'OTD 납기준수율',  value: otdRate,   unit: '%',  color: otdRate >= 90 ? '#388e3c' : '#f44336' },
    { label: '평균 리드타임',   value: avgLead,   unit: '일', color: '#f57c00' },
  ];
};

const DeliveryTab = () => (
  <KpiReportTemplate
    title="납기 KPI (OTD)"
    fetchFn={kpiApi.getDeliveryKpi}
    chartConfig={deliveryChartConfig}
    columns={deliveryColumns}
    summaryFn={deliverySummaryFn}
  />
);

// ── 원가 탭 (기존 KpiCostPage의 내용을 그대로 임베드) ──────────────
const CostTab = React.lazy(() => import('./KpiCostContent'));

export default KpiDashboardPage;
